import { defineChatPlace } from '../../../chat/panel';
import { boardChat } from '../boardChat';
import type { LookupDashboardPayload } from '../../../types/lookupDashboard';

/**
 * The chat docked beside a lookup board. The same place as the cohort
 * board's — same features, same panel switch — with starters for looking
 * things up rather than reading cohorts.
 */
export const lookupChat = defineChatPlace({
  ...boardChat,
  welcome: { title: '', subtitle: 'Ask about the records on this board, or look up another.' },
  // The two jobs Goodwin's sourcing team named: a price to negotiate from,
  // and an operator for a route nobody picked.
  starters: [
    'What has each operator charged on this route, most recent first?',
    'Which operators have flown the return leg?',
    'Who has flown from Hilton Head to Tallahassee?',
    'Is the latest price above what this route usually costs?',
  ],
  placeholder: 'Ask about this route…',
});

/**
 * The chat for one board: its spec's own welcome, placeholder and starters —
 * Operator Performance asks about operators, not routes — falling back to
 * the route board's for anything the spec leaves out.
 */
export function lookupChatFor(payload: LookupDashboardPayload) {
  const own = payload.spec.chat ?? {};
  if (!own.welcome && !own.placeholder && !own.starters?.length) return lookupChat;
  return defineChatPlace({
    ...lookupChat,
    welcome: { ...lookupChat.welcome, subtitle: own.welcome ?? lookupChat.welcome?.subtitle ?? '' },
    starters: own.starters?.length ? own.starters : lookupChat.starters,
    placeholder: own.placeholder ?? lookupChat.placeholder,
  });
}

/**
 * What the agent is told the analyst is looking at: the inputs and the
 * figures on screen, not the records themselves — `lookup_route` reads those.
 */
export function buildLookupSurfaceContext(
  dashboardId: string,
  payload: LookupDashboardPayload,
): Record<string, unknown> {
  const summary = payload.blocks.find((b) => b.type === 'summary');
  const rankings = Object.fromEntries(
    payload.blocks
      .filter((b) => b.type === 'ranking')
      .map((b) => [b.id, b.type === 'ranking'
        // A second label tells apart rows that share a first one ("Maintenance (Mx)").
        ? b.rows.slice(0, 5).map((r) => ({ name: r.label, ...(r.sub_label !== undefined && { sub: r.sub_label }), ...r.values }))
        : []]),
  );
  return {
    type: 'manual_dashboard',
    kind: 'lookup',
    dashboard_id: dashboardId,
    // Lets the server give the chat this board's own guidance and tools.
    spec_key: payload.spec.key,
    name: payload.spec.name,
    inputs: payload.inputs.map((i) => ({ key: i.key, label: i.label, value: i.value })),
    // Filters on screen, by key — the chat's lookups use them unless told otherwise.
    filters: Object.fromEntries((payload.filters ?? []).filter((f) => f.selected.length).map((f) => [f.key, f.selected])),
    // The picked date range, both ends inclusive; left out for all time.
    ...(payload.date_filter?.start || payload.date_filter?.end
      ? { dates: { start: payload.date_filter.start, end: payload.date_filter.end } }
      : {}),
    // The weekday picked on a board with a day picker, e.g. "Mon".
    ...(payload.day_picker ? { day: payload.day_picker.value } : {}),
    // The time zone picked on a board with a zone picker, e.g. "CT".
    ...(payload.zone_picker ? { zone: payload.zone_picker.value } : {}),
    ready: payload.ready,
    // The board opened on its most common inputs, not something the analyst typed.
    defaulted: payload.defaulted,
    scope_note: payload.spec.scope_note,
    source_label: payload.spec.source_label,
    matched_rows: payload.meta.record_count ?? payload.meta.matched_rows,
    as_of: payload.meta.as_of,
    headline:
      summary?.type === 'summary'
        ? summary.tiles.map((t) => ({
            label: t.label, value: t.value, note: t.note,
            ...(t.comparison ? { compared_with: { label: t.comparison.label, value: t.comparison.value } } : {}),
          }))
        : [],
    rankings,
  };
}
